import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

import microscopeAsset from "@/assets/restore/values-mission/mission-microscope.jpg.asset.json";
import surgeryAsset from "@/assets/restore/values-mission/mission-surgery.jpg.asset.json";
import consultAsset from "@/assets/restore/values-mission/mission-consult.webp.asset.json";
import tabletAsset from "@/assets/restore/values-mission/mission-tablet.jpg.asset.json";
import lovittAsset from "@/assets/restore/values-mission/mission-lovitt.webp.asset.json";
import hofAsset from "@/assets/restore/values-mission/hof-athletes.jpg.asset.json";
const espnAsset = { url: "/press-logos/espn.webp" };
const esquireAsset = { url: "/press-logos/esquire.webp" };
const foxAsset = { url: "/press-logos/fox-sports.webp" };
const gqAsset = { url: "/press-logos/gq.webp" };
const nbcAsset = { url: "/press-logos/nbc.webp" };
import ianBeforeAsset from "@/assets/restore/ian-before.webp.asset.json";
import ianAfterAsset from "@/assets/restore/ian-after.webp.asset.json";
import mattBeforeAsset from "@/assets/restore/matt-before.webp.asset.json";
import mattAfterAsset from "@/assets/restore/matt-after.webp.asset.json";
import zachBeforeAsset from "@/assets/restore/zach-before.webp.asset.json";
import zachAfterAsset from "@/assets/restore/zach-after.webp.asset.json";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

const TITLE = "Values & Mission | RESTORE® Hair";
const DESCRIPTION =
  "Read the RESTORE Hair values and mission. Our hair restoration work is led by medical director Dr. James Harris and the RESTORE team.";

const coreValues = [
  {
    title: "People First",
    body: "At RESTORE, every patient is treated as a person, not a case or a procedure. We recognize the emotional impact hair loss can have on confidence, identity, and morale, and we approach every interaction with care, patience, and respect.",
  },
  {
    title: "Individualized Care",
    body: "No two hair loss journeys are the same. Each treatment plan is thoughtfully designed around the patient's unique hair pattern, goals, lifestyle, and long-term outlook, because personalized care leads to better outcomes and greater confidence.",
  },
  {
    title: "Empathy & Compassion",
    body: "We listen first. Hair loss is personal, and often vulnerable. Our team creates a supportive, judgment-free environment where patients feel heard, understood, and comfortable asking questions at every step of the process.",
  },
  {
    title: "Education & Empowerment",
    body: "We believe education builds confidence. RESTORE prioritizes transparency and patient education, helping individuals understand their options, timelines, and expectations so they can make informed decisions that feel right for them.",
  },
  {
    title: "Integrity & Trust",
    body: "Trust is earned through honesty and consistency. We provide clear guidance, realistic expectations, and recommendations rooted in medical responsibility and long-term results.",
  },
  {
    title: "Excellence & Expertise",
    body: "Our experienced physicians and clinical teams are committed to precision, continuous learning, and high standards of care. By combining expertise with modern techniques, we deliver results that look natural and stand the test of time.",
  },
];

const whyClients = [
  { title: "Hall of Fame Hockey Player", name: "Eddie Olczyk" },
  { title: "Hall of Fame Football Player", name: "Deion Sanders" },
  { title: "Hall of Fame Football Player", name: "Brian Urlacher" },
  { title: "Professional Baseball Player", name: "Ian Happ" },
];

const braggingLogos = [
  { src: espnAsset.url, alt: "ESPN" },
  { src: esquireAsset.url, alt: "Esquire" },
  { src: foxAsset.url, alt: "Fox Sports" },
  { src: gqAsset.url, alt: "GQ" },
  { src: nbcAsset.url, alt: "NBC" },
];

const vmResults = [
  {
    grafts: "2,152 Grafts",
    name: "Ian Happ",
    role: "Chicago Baseball Player",
    before: ianBeforeAsset.url,
    after: ianAfterAsset.url,
    href: "https://restorehair.com/results/ian-happ/",
    quote:
      "I never wore my hat in the dugout. When I would see photos of myself during the game, my hair looked like a mess.",
    cta: "Watch Ian's Story",
  },
  {
    grafts: "6,800 Grafts",
    name: "Matt Forte",
    role: "Ret. Football Player",
    before: mattBeforeAsset.url,
    after: mattAfterAsset.url,
    href: "https://restorehair.com/results/matt-forte/",
    quote:
      "It's almost like a rite of passage for a kid, when they go to the barbershop with their dad. I am glad I am able to take my son with me now.",
    cta: "Watch Matt's Story",
  },
  {
    grafts: "4,805 Grafts",
    name: "Zach Johnson",
    role: "Pro Tour Golfer",
    before: zachBeforeAsset.url,
    after: zachAfterAsset.url,
    href: "https://restorehair.com/results/zach-johnson/",
    quote:
      "People can't believe how young I look. I think it's taken about 10 years off of me. I am always camera & spotlight ready!",
    cta: "Watch Zach's Story",
  },
];

export const Route = createFileRoute("/values-mission")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://restorehair.com/values-mission/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/values-mission/" }],
  }),
  component: ValuesMissionPage,
});

function ValuesMissionPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Mission */}
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h1 className="text-center text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
              Our Mission
            </h1>
            <div className="mt-8 space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                At RESTORE, our mission is to help people feel confident in their appearance through
                medically responsible hair restoration, led by experienced physicians who specialize
                exclusively in hair loss and restoration.
              </p>
              <p>
                Our board-certified doctors bring years of clinical expertise, precision, and care to
                every procedure. By combining sophisticated surgical techniques, modern technology, and a
                deep understanding of hair loss progression, we deliver natural-looking, long-term
                results tailored to each individual.
              </p>
              <p>
                We believe hair loss is not just a cosmetic concern. It's a deeply personal
                experience. That's why we take a thoughtful, patient-first approach, providing
                clarity in a space often filled with misinformation, shortcuts, and unrealistic
                promises. Every recommendation we make is grounded in medical expertise and what is
                truly best for the patient (not trends or one-size-fits-all solutions).
              </p>
            </div>

            <div className="mt-12 grid gap-4 md:grid-cols-3">
              <img
                src={microscopeAsset.url}
                alt="RESTORE technician examining follicular grafts under a microscope"
                loading="lazy"
                className="h-64 w-full object-cover md:h-full"
              />
              <div className="grid gap-4">
                <img
                  src={surgeryAsset.url}
                  alt="Dr. James Harris performing a RESTORE hair transplant procedure"
                  loading="lazy"
                  className="h-64 w-full object-cover"
                />
                <img
                  src={lovittAsset.url}
                  alt="Dr. Shelly Lovitt consulting with a RESTORE patient"
                  loading="lazy"
                  className="h-64 w-full object-cover"
                />
              </div>
              <div className="grid gap-4">
                <img
                  src={consultAsset.url}
                  alt="RESTORE hair restoration consultation"
                  loading="lazy"
                  className="h-64 w-full object-cover"
                />
                <img
                  src={tabletAsset.url}
                  alt="RESTORE physician reviewing a scalp analysis with a patient"
                  loading="lazy"
                  className="h-64 w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core values */}
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <h2 className="text-center text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
              Our Core Values
            </h2>
            <div className="mt-10 grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
              {coreValues.map((v) => (
                <div key={v.title} className="bg-muted px-8 py-10 text-center">
                  <h3 className="text-2xl font-bold text-foreground">{v.title}</h3>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why RESTORE */}
        <section className="pt-12 md:pt-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-4xl tracking-tight text-foreground md:text-5xl">
              Why <strong className="font-extrabold">RESTORE?</strong>
            </h2>
            <p className="mx-auto mt-5 max-w-4xl text-center text-xl leading-relaxed text-foreground md:text-2xl">
              From celebrities and athletes to businessmen and everyman, more than 20,000 men from
              all walks of life{" "}
              <strong className="font-extrabold">have regrown their hair thanks to RESTORE.</strong>
            </p>
            <div className="mt-10 grid gap-6 text-center sm:grid-cols-2 lg:grid-cols-4">
              {whyClients.map((c) => (
                <div key={c.name}>
                  <span className="inline-block bg-primary/30 px-3 py-1 text-sm text-foreground">
                    {c.title}
                  </span>
                  <p className="mt-2 text-xl font-extrabold text-foreground">{c.name}</p>
                  <p className="text-sm italic text-muted-foreground">RESTORE Client</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto mt-6 max-w-6xl px-4 lg:px-8">
            <div className="relative overflow-hidden rounded-lg">
              <img
                src={hofAsset.url}
                alt="RESTORE clients Eddie Olczyk, Deion Sanders, Brian Urlacher and Ian Happ"
                loading="lazy"
                className="h-auto w-full object-cover"
              />
              <div className="bg-[hsl(0_0%_35%)] px-6 py-8 md:absolute md:inset-x-0 md:bottom-0">
                <p className="text-center text-xl tracking-wide text-white">BRAGGING RIGHTS</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-8 md:gap-14">
                  {braggingLogos.map((l) => (
                    <img
                      key={l.alt}
                      src={l.src}
                      alt={`${l.alt} logo`}
                      loading="lazy"
                      className="h-8 w-auto object-contain md:h-10"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="bg-background py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="mx-auto max-w-5xl text-center text-sm italic text-muted-foreground">
              100% authentic RESTORE clients. No photoshopping. No trick photography. No Stock
              photos.
            </p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {vmResults.map((r) => (
                <div key={r.name} className="flex flex-col">
                  <div className="relative overflow-hidden">
                    <div className="grid h-[250px] w-full grid-cols-2 bg-white">
                      <img
                        src={r.before}
                        alt={`${r.name} before hair restoration`}
                        loading="lazy"
                        width={500}
                        height={500}
                        className="block h-full w-full object-cover object-center"
                      />
                      <img
                        src={r.after}
                        alt={`${r.name} after hair restoration`}
                        loading="lazy"
                        width={500}
                        height={500}
                        className="block h-full w-full object-cover object-center"
                      />
                    </div>
                    <div className="bg-foreground/70 px-[15px] py-[6px] text-background">
                      <div className="text-[15px] font-bold leading-6 text-background">{r.grafts}</div>
                      <div className="text-[30px] font-normal leading-[40px]">{r.name}</div>
                      <div className="mt-[1px] text-base italic leading-5 text-background">{r.role}</div>
                      <div className="text-base italic leading-5 text-background">RESTORE® Client</div>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-col items-center bg-muted/40 px-6 py-8 text-center">
                    <span className="text-5xl font-serif leading-none text-muted-foreground/40">"</span>
                    <p className="mt-2 text-base leading-relaxed text-foreground">{r.quote}</p>
                    <a
                      href={r.href}
                      className="mt-6 inline-block bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition hover:bg-primary/90"
                    >
                      {r.cta}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <p className="mx-auto mt-8 max-w-5xl text-center text-xs italic leading-relaxed text-muted-foreground">
              Not all patients will experience the outcomes shown in the photographs and described in
              the testimonial statements on this website, and not all persons will be eligible for
              the procedure. Results will depend on a number of factors, including your personal
              medical history. Please ask a physician about your individual case. Testimonials were
              compensated for their endorsements.
            </p>
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
